import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios, { InternalAxiosRequestConfig } from "axios";
import dayjs from "dayjs";

import useQueryBoard from "../../lib/hooks/useQueryBoard";

import logger from "../../lib/logger";
import { IIntegration, Paxton, PaxtonAuth } from "./types";

const PAXTON_INTEGRATION = "paxton";

export const isPaxtonIntegration = (
  data: IIntegration | undefined,
): data is Paxton => {
  return data?.type === PAXTON_INTEGRATION;
};

const hasTokenExpired = (expiryDatetime: string | undefined) => {
  return (
    (!!expiryDatetime &&
      dayjs(expiryDatetime).subtract(10, "m").isBefore(dayjs())) ??
    true
  );
};

interface PaxtonContextValue {
  openDoor: () => Promise<boolean>;
}

const PaxtonContext = React.createContext<PaxtonContextValue>(null!);

export const usePaxtonContext = () => useContext(PaxtonContext);

interface PaxtonProviderProps {
  children: React.ReactNode;
}

export const PaxtonProvider = React.memo<PaxtonProviderProps>((props) => {
  const { children } = props;

  const { data } = useQueryBoard();
  const [paxtonConfig, setPaxtonConfig] = useState<Paxton>();
  const [paxtonAuth, setPaxtonAuth] = useState<PaxtonAuth>();

  // config parsing
  useEffect(() => {
    const accessControl = data?.digitalReception?.metadata?.accessControl;

    if (!isPaxtonIntegration(accessControl)) {
      return;
    }

    setPaxtonConfig(() => accessControl);
  }, [data]);

  // parsing token from local storage
  useEffect(() => {
    if (!paxtonConfig) {
      return;
    }

    const { expiry_datetime } = paxtonAuth || {};
    const paxtonAccessData = localStorage.getItem("paxton_access_data");
    if (!paxtonAccessData) {
      return;
    }

    if (hasTokenExpired(expiry_datetime)) {
      authenticate();
      return;
    }

    setPaxtonAuth(() => JSON.parse(atob(paxtonAccessData)) as PaxtonAuth);
  }, [paxtonConfig]);

  // axios setup
  const paxtonClient = useMemo(() => {
    if (!paxtonConfig) {
      return;
    }

    const client = axios.create({
      baseURL: paxtonConfig.address,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return client;
  }, [paxtonConfig]);

  // interceptor declaration
  const requestInterceptor = useCallback(
    async (config: InternalAxiosRequestConfig) => {
      const { clientId: client_id, address } = paxtonConfig || {};
      const { access_token, expiry_datetime, refresh_token } = paxtonAuth || {};

      if (!config.baseURL) {
        config.baseURL = address;
      }

      if (access_token) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }

      if (expiry_datetime && hasTokenExpired(expiry_datetime)) {
        try {
          const { data: authenticationData } = await axios.post(
            `${address}/api/v1/authorization/tokens`,
            {
              refresh_token,
              grant_type: "refresh_token",
              ...(client_id ? { client_id } : {}),
              scope: "offline_access",
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            },
          );

          setPaxtonAuth(() => authenticationData);
          localStorage.setItem(
            "paxton_access_data",
            btoa(JSON.stringify(authenticationData)),
          );

          const { access_token: accessToken } = authenticationData;
          config.headers.Authorization = `Bearer ${accessToken}`;

          if (accessToken) {
            //@ts-ignore
            connection.accessToken = accessToken;
          }
        } catch (error) {
          localStorage.removeItem("paxton_access_data");
          return Promise.reject(error);
        }
      }

      return config;
    },
    [paxtonConfig, paxtonAuth],
  );

  // interceptor setup
  useEffect(() => {
    if (!paxtonClient || !paxtonAuth || !paxtonConfig || !requestInterceptor) {
      return;
    }

    const interceptorId =
      paxtonClient.interceptors.request.use(requestInterceptor);

    return () => {
      paxtonClient.interceptors.request.eject(interceptorId);
    };
  }, [requestInterceptor, paxtonClient, paxtonAuth, paxtonConfig]);

  const openDoor = useCallback(async () => {
    if (!paxtonClient || !paxtonConfig) {
      return false;
    }

    try {
      await paxtonClient.post("/api/v1/commands/door/open", {
        DoorId: paxtonConfig.doorId,
      });

      return true;
    } catch (error) {
      logger.debug("[Paxton Client Integration] openDoor error", error);
    }
    return false;
  }, [paxtonAuth, paxtonConfig]);

  // authentication
  const authenticate = useCallback(async () => {
    if (!paxtonConfig) {
      return;
    }

    const { address, username, password, clientId } = paxtonConfig || {};

    try {
      const { data: authenticationData } = await axios.post<PaxtonAuth>(
        `${address}/api/v1/authorization/tokens`,
        {
          username,
          password,
          ...(clientId ? { client_id: clientId + "" } : {}),
          grant_type: "password",
          scope: "offline_access",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      setPaxtonAuth(() => authenticationData);
      localStorage.setItem(
        "paxton_access_data",
        btoa(JSON.stringify(authenticationData)),
      );
    } catch (error) {
      logger.error(
        `[Paxton Client Integration] Authentication failed: `,
        error,
      );
    }
  }, [paxtonConfig]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (paxtonAuth) {
        clearInterval(interval);
        return;
      }

      authenticate();
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  });

  const value = useMemo(
    () => ({
      openDoor,
    }),
    [data, paxtonAuth, paxtonConfig],
  );

  return (
    <PaxtonContext.Provider value={value}>{children}</PaxtonContext.Provider>
  );
});

export default PaxtonProvider;
